import React, { useMemo, useCallback, useState, useEffect } from "react";
import { useNavigate } from "react-router";
import isEmpty from "lodash/isEmpty";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import { Formik, Form, useFormikContext } from "formik";

import Header from "../../../../../../components/wizard/Header";
import Footer from "../../../../../../components/wizard/Footer/Footer";
import { getWO } from "../../../../utils";
import useMutation from "../../../../../../hooks/useMutation";
import { UPDATE_INVENTORY_REQUEST_MUTATION } from "../../../../../../constants/graphql/mutations";
import { getChargeOnlyFields } from "../../../../utils";
import ChargeSection from "../../../../../../components/capture/ReceivingWizard/AdditionalCharges/ChargeSection";
import EmptyAdditionalCharges from "../../../../../../components/capture/ReceivingWizard/AdditionalCharges/EmptyAdditionalCharges";
import RateCategoryPickerModal from "../../../../../../components/capture/ReceivingWizard/AdditionalCharges/RateCategoryPickerModal";
import RateMultiSelectModal from "../../../../../../components/capture/ReceivingWizard/AdditionalCharges/RateMultiSelectModal";
import OneTimeChargesModal from "../../../../../../components/project/inbound/OneTimeChargesModal";
import {
  getSectionsWithOneTimeCharges,
  buildInitialValues,
  validateCharges,
} from "../../../../../../components/capture/ReceivingWizard/AdditionalCharges/utils";
import { categories } from "../../../../../../components/capture/ReceivingWizard/AdditionalCharges/constants";
import useHasEnabledIntegration from "src/hooks/useHasEnabledIntegration";

function AncillaryChargesForm({ inventoryRequest, project, onNoAdditionalCharges, verticalLayout = false, hideOneTimeCharges, setHideOneTimeCharges }) {
  const navigate = useNavigate();
  const { hasIntegration } = useHasEnabledIntegration(project);
  const { isSubmitting, values, setValues } = useFormikContext();
  const [openCategoryPicker, setOpenCategoryPicker] = useState(false);
  const [openRatePicker, setOpenRatePicker] = useState(null);
  const [openOneTime, setOpenOneTime] = useState(false);
  const list = Object.entries(values).map(([key, value]) => ({
    key,
    entries: Object.entries(value),
  }));
  const outboundCategories = categories.filter(cat => cat.key !== "openInspectRates");

  const handleAddBillingCharges = () => {
    if (!inventoryRequest?.oneTimeCharges?.no_additional_charges) {
      setHideOneTimeCharges(false);
    }
    setOpenCategoryPicker(true);
  };

  const handleSelectCategory = categoryKey => {
    setOpenCategoryPicker(false);
    if (categoryKey === "oneTimeCharge") {
      setOpenOneTime(true);
    } else if (categoryKey === "noAdditionalCharges") {
      onNoAdditionalCharges();
    } else {
      setOpenRatePicker({ categoryKey });
    }
  };

  const handleBack = () => {
    const params = new URLSearchParams(window.location.search);
    params.set("step", hasIntegration ? "integration" : "items");
    navigate({ search: params.toString() });
  };

  const hasNoChargesConfirmed = inventoryRequest?.oneTimeCharges?.no_additional_charges !== undefined;
  const isFormValid = list.length > 0 || hasNoChargesConfirmed;

  return (
    <>
      <Form id="ancillary-charges-form" style={{ height: "100%" }}>
        <Box display="flex" flexDirection="column" justifyContent="space-between" minHeight="100%">
          <Header title="Ancillary Charges" info={getWO(inventoryRequest)} />
          <Box px={2} mt={2} flexGrow={1} sx={{ overflowY: "auto", minHeight: 320 }}>
            {inventoryRequest?.oneTimeCharges?.no_additional_charges !== undefined &&
            !hideOneTimeCharges ? (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight={320}
                gap={2}
              >
                <Typography variant="h6" color="success.main" align="center">
                  ✓ No Ancillary Charges Confirmed
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center">
                  This outbound order has been confirmed to have no ancillary charges.
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setHideOneTimeCharges(true);
                    setOpenCategoryPicker(true);
                  }}
                  size="small"
                >
                  Add Charges Instead
                </Button>
              </Box>
            ) : isEmpty(values) ? (
              <EmptyAdditionalCharges
                description="Outbound orders may include extra charges for materials, labor, or inspection services."
                onAddBillingCharges={handleAddBillingCharges}
              />
            ) : (
              <>
                {list.map(sec => <ChargeSection key={sec.key} section={sec} timeIncrementMinutes={project?.settings?.time_increment_minutes} />)}
                <Box mt={2}>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddBillingCharges}
                  >
                    Add More Charges
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Form>
      <Box mt={2}>
        <Footer
          step={2}
          steps={4}
          startTime={inventoryRequest?.pickingStartTime}
          completeTime={inventoryRequest?.pickingCompleteTime}
          showRaceMode={!verticalLayout}
        >
          <div style={{ width: verticalLayout ? "100%" : "auto" }}>
            {!verticalLayout && (
              <Button
                onClick={handleBack}
                variant="outlined"
                sx={{ mr: 1, textTransform: "none" }}
              >
                Back
              </Button>
            )}
            <Button
              type="submit"
              variant="contained"
              form="ancillary-charges-form"
              sx={{ textTransform: "none" }}
              disabled={isSubmitting || !isFormValid}
              fullWidth={verticalLayout}
            >
              {verticalLayout ? "Next Step" : "Next"}
            </Button>
          </div>
        </Footer>
      </Box>
      {openCategoryPicker && (
        <RateCategoryPickerModal
          onClose={() => setOpenCategoryPicker(false)}
          onSelect={handleSelectCategory}
          hasExistingCharges={list.length > 0}
          categories={outboundCategories}
        />
      )}
      {openRatePicker && (
        <RateMultiSelectModal
          onClose={() => setOpenRatePicker(null)}
          categoryKey={openRatePicker.categoryKey}
          rates={project?.[openRatePicker.categoryKey] || {}}
          initialSelected={Object.keys(
            {
              materialRates: values?.materialCharges,
              laborRates: values?.laborCharges,
              openInspectRates: values?.openInspectCharges,
              equipmentRates: values?.equipmentCharges,
            }[openRatePicker.categoryKey] || {}
          )}
        />
      )}
      {openOneTime && (
        <OneTimeChargesModal
          shipment={inventoryRequest}
          onClose={() => setOpenOneTime(false)}
          onSubmit={submitted => {
            const nextValues = {
              ...values,
              oneTimeCharges: {
                ...(values?.oneTimeCharges || {}),
                ...Object.fromEntries(submitted.list.map(v => [v.notes, v.amount])),
              },
            };
            setValues(nextValues, false);
            setOpenOneTime(false);
          }}
        />
      )}
    </>
  );
}

export default function AncillaryCharges({ project, inventoryRequest, verticalLayout = false }) {
  const navigate = useNavigate();
  const [updateInventoryRequest] = useMutation(UPDATE_INVENTORY_REQUEST_MUTATION);
  const [hideOneTimeCharges, setHideOneTimeCharges] = useState(false);

  useEffect(() => {
    const warehouses = inventoryRequest?.warehousesFromInventoryItems || [];
    const allWarehousesExcluded = warehouses.length > 0 && warehouses.every(w => w.excludeFromBilling);
    if (allWarehousesExcluded) {
      const params = new URLSearchParams(window.location.search);
      params.set("step", "photos");
      navigate({ search: params.toString() }, { replace: true });
    }
  }, [inventoryRequest, navigate]);
  const sections = useMemo(
    () => {
      const allSections = getSectionsWithOneTimeCharges(project, "outbound_enabled", inventoryRequest);
      return allSections.filter(section => section.key !== "openInspectCharges");
    },
    [project, inventoryRequest]
  );

  const initialValues = useMemo(
    () => buildInitialValues(sections, inventoryRequest),
    [sections, inventoryRequest]
  );

  const hasNoChargesConfirmed = inventoryRequest?.oneTimeCharges?.no_additional_charges !== undefined;
  const skipValidation = hasNoChargesConfirmed && !hideOneTimeCharges;

  const handleNoAdditionalCharges = useCallback(async () => {
    const noChargesValues = {
      oneTimeCharges: { no_additional_charges: 0 },
      billingQuantities: inventoryRequest.billingQuantities,
    };

    try {
      await updateInventoryRequest({
        variables: {
          id: inventoryRequest.id,
          input: noChargesValues,
        },
      });

      const params = new URLSearchParams(window.location.search);
      params.set("step", "photos");
      navigate({ search: params.toString() });
    } catch (error) {
      console.error("Failed to save no ancillary charges:", error);
    }
  }, [updateInventoryRequest, inventoryRequest, navigate]);

  const handleSubmit = useCallback(
    async (values, { setSubmitting }) => {
      const inputPayload = {};

      Object.entries(values).forEach(([sectionKey, sectionValues]) => {
        const obj = {};
        Object.entries(sectionValues).forEach(([rateKey, value]) => {
          if (value !== "" && value !== undefined && value !== null) {
            obj[rateKey] = Number(value);
          }
        });

        if (Object.keys(obj).length > 0) {
          if (sectionKey === "oneTimeCharges") {
            inputPayload[sectionKey] = {
              list: Object.entries(obj).map(([notes, amount]) => ({ notes, amount })),
            };
          } else {
            inputPayload[sectionKey] = obj;
          }
        }
      });

      const hasActualCharges = Object.keys(inputPayload).some(key =>
        ["materialCharges", "laborCharges", "equipmentCharges", "oneTimeCharges"].includes(key)
      );


      if (hasActualCharges && inventoryRequest?.oneTimeCharges?.no_additional_charges !== undefined) {
        if (!inputPayload.oneTimeCharges) {
          inputPayload.oneTimeCharges = { list: [] };
        }
      }

      // Preserve billingQuantities to prevent it from being cleared
      inputPayload.billingQuantities = inventoryRequest.billingQuantities;
      await updateInventoryRequest({
        variables: {
          id: inventoryRequest.id,
          input: inputPayload,
        },
      });

      setSubmitting(false);

      const params = new URLSearchParams(window.location.search);
      params.set("step", "photos");
      navigate({ search: params.toString() });
    },
    [sections, updateInventoryRequest, inventoryRequest, navigate]
  );

  return (
    <Formik
      initialValues={initialValues}
      validate={values => validateCharges(values, skipValidation)}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      <AncillaryChargesForm
        inventoryRequest={inventoryRequest}
        project={project}
        onNoAdditionalCharges={handleNoAdditionalCharges}
        verticalLayout={verticalLayout}
        hideOneTimeCharges={hideOneTimeCharges}
        setHideOneTimeCharges={setHideOneTimeCharges}
      />
    </Formik>
  );
}
